import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import '../styles/GroupCreationModal.css';

function GroupCreationModal({ isOpen, onClose, onGroupCreated, user, socket }) {
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchContacts();
    }
  }, [isOpen, user]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/groups/contacts/${user.id}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to fetch contacts');
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleContactToggle = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }

    if (selectedContacts.length === 0) {
      setError('Please select at least one contact');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', groupName.trim());
      formData.append('description', groupDescription.trim());
      formData.append('memberIds', JSON.stringify(selectedContacts));
      formData.append('creatorId', user.id);
      
      // Add avatar file if selected
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      const response = await axios.post(`${config.apiUrl}/api/groups`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Send welcome message to the group
      try {
        const welcomeMessage = {
          from: user.id,
          groupId: response.data._id,
          text: `Welcome to ${response.data.name}! 🎉`,
          messageType: 'text'
        };
        
        // Emit the welcome message via socket
        if (socket) {
          socket.emit('sendGroupMessage', welcomeMessage);
        }
      } catch (error) {
        //console.log('Could not send welcome message:', error);
      }

      onGroupCreated(response.data);
      handleClose();
    } catch (error) {
      console.error('Error creating group:', error);
      setError(error.response?.data?.error || 'Failed to create group');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setGroupName('');
    setGroupDescription('');
    setSelectedContacts([]);
    setSearchTerm('');
    setError('');
    setAvatarFile(null);
    setAvatarPreview(null);
    onClose();
  };

  const filteredContacts = Array.isArray(contacts) ? contacts.filter(contact =>
    contact.username.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Group</h2>
          <button className="close-button" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="groupName">Group Name *</label>
            <input
              type="text"
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              maxLength="50"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="groupAvatar">Group Avatar (Optional)</label>
            <div className="avatar-upload-container">
              <input
                type="file"
                id="groupAvatar"
                accept="image/*"
                onChange={handleAvatarChange}
                className="avatar-input"
              />
              <label htmlFor="groupAvatar" className="avatar-upload-label">
                {avatarPreview ? (
                  <div className="avatar-preview">
                    <img src={avatarPreview} alt="Avatar preview" />
                    <button type="button" onClick={removeAvatar} className="remove-avatar">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="avatar-placeholder">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                    <span>Choose Image</span>
                  </div>
                )}
              </label>
            </div>
            <small>Recommended size: 200x200 pixels, max 5MB</small>
          </div>

          <div className="form-group">
            <label htmlFor="groupDescription">Description (Optional)</label>
            <textarea
              id="groupDescription"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              placeholder="Enter group description"
              maxLength="200"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Select Contacts ({selectedContacts.length} selected)</label>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="contacts-list">
              {filteredContacts.length === 0 ? (
                <div className="no-contacts">
                  {searchTerm ? 'No contacts found matching your search' : 'No contacts available'}
                </div>
              ) : (
                filteredContacts.map(contact => (
                  <div
                    key={contact._id}
                    className={`contact-item ${selectedContacts.includes(contact._id) ? 'selected' : ''}`}
                    onClick={() => handleContactToggle(contact._id)}
                  >
                    <div className="contact-avatar">
                      {contact.profileImage ? (
                        <img 
                          src={`${config.apiUrl}${contact.profileImage}`} 
                          alt={contact.username}
                          className="contact-profile-image"
                        />
                      ) : (
                        <div className="contact-avatar-placeholder notranslate">
                          {contact.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="contact-info">
                      <div className="contact-name notranslate">{contact.username}</div>
                    </div>
                    <div className="contact-checkbox">
                      {selectedContacts.includes(contact._id) && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={handleClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="create-button" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default GroupCreationModal;

